import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import {
  Leaf,
  Clock,
  MapPin,
  Phone,
  Mail,
  TrendingUp,
  Recycle,
  Calendar,
  Award,
  Info,
  UserPlus,
  LogIn,
  LogOut,
  Loader2,
  QrCode,
} from 'lucide-react';
import Footer from '../components/Footer';
import { getEcoparqueSession, clearEcoparqueSession } from '../utils/ecoparqueSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INFO_ECOPARQUE = [
  {
    icon: Clock,
    title: 'Horarios',
    content: 'Lunes a viernes: 08:00–15:00 y 16:00–19:00 | Sábados: 09:00–14:00 | Domingos y festivos: cerrado',
  },
  {
    icon: MapPin,
    title: 'Ubicación',
    content: 'Polígono Industrial Los Alcázares, Calle de la Industria s/n, 30710 Los Alcázares, Murcia',
  },
  {
    icon: Recycle,
    title: 'Qué puedes traer',
    content: 'Papel/Cartón, Vidrio, Plásticos, Orgánico, Aceite, Pilas, Electrónicos, Voluminosos y Poda',
  },
];

export default function Ecoparque() {
  const navigate = useNavigate();
  const [session, setSession] = useState(getEcoparqueSession());
  const [stats, setStats] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('entregas');

  useEffect(() => {
    if (session?.user_code) {
      fetchData(session.user_code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (code) => {
    setLoading(true);
    try {
      const [sRes, dRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/ecoparque/users/${encodeURIComponent(code)}/stats`),
        fetch(`${BACKEND_URL}/api/ecoparque/users/${encodeURIComponent(code)}/deliveries?limit=50`),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (dRes.ok) setDeliveries(await dRes.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearEcoparqueSession();
    setSession(null);
    setStats(null);
    setDeliveries([]);
    navigate('/ecoparque/login');
  };

  const total = useMemo(() => {
    if (!stats) return { kg: 0, points: 0, entries: 0 };
    return { kg: stats.total_kg, points: stats.total_points, entries: stats.total_entries };
  }, [stats]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Mi Ecoparque | Cuidemos Los Alcázares®</title>
        <meta
          name="description"
          content="Portal ciudadano del Ecoparque de Los Alcázares: registro con QR, historial de entregas y puntos PAYT (Pay-As-You-Throw)."
        />
      </Helmet>

      {/* Hero */}
      <section
        className="relative pt-24 pb-16 px-6 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1F8A70 0%, #1E5AA8 100%)' }}
      >
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Leaf size={28} className="text-white" />
                </div>
                <span className="text-white/90 text-sm font-medium uppercase tracking-wider">Ciudadanía</span>
              </div>
              <h1 className="font-display text-5xl sm:text-6xl text-white mb-3">Mi Ecoparque</h1>
              {session ? (
                <p className="text-white/85 text-base sm:text-lg max-w-2xl leading-relaxed">
                  Hola <strong>{session.nombre}</strong> ·{' '}
                  <span className="font-mono bg-white/15 px-2 py-0.5 rounded" data-testid="eco-dashboard-user-code">
                    {session.user_code}
                  </span>
                </p>
              ) : (
                <p className="text-white/80 text-base sm:text-lg max-w-2xl leading-relaxed">
                  Regístrate o accede para consultar tus entregas y puntos PAYT.
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              {session ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/15 border border-white/30 text-white text-sm font-medium hover:bg-white/25 transition-colors"
                  data-testid="eco-dashboard-logout-btn"
                >
                  <LogOut size={16} /> Salir
                </button>
              ) : (
                <>
                  <Link
                    to="/ecoparque/registro"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#1E5AA8] text-sm font-semibold hover:bg-[#F5F9FC] transition-colors"
                    data-testid="eco-dashboard-cta-registro"
                  >
                    <UserPlus size={16} /> Registrarme
                  </Link>
                  <Link
                    to="/ecoparque/login"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/15 border border-white/30 text-white text-sm font-medium hover:bg-white/25 transition-colors"
                    data-testid="eco-dashboard-cta-login"
                  >
                    <LogIn size={16} /> Acceder
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Info rápida */}
      <section className="py-12 px-6 bg-[#FAFCFE]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {INFO_ECOPARQUE.map(({ icon: Icon, title, content }) => (
              <div
                key={title}
                className="rounded-2xl border border-[#CFE3F5] bg-white p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[rgba(30,90,168,0.12)] flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-[#1E5AA8]" />
                  </div>
                  <div>
                    <h3 className="font-sans font-semibold text-[#0A2540] mb-2">{title}</h3>
                    <p className="text-sm text-[#6F8AA8] leading-relaxed">{content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {!session ? (
        /* CTA para usuarios no registrados */
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-3xl border-2 border-[#1F8A70] bg-gradient-to-br from-[#F0FBF6] to-white p-8 sm:p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#1F8A70]/15 flex items-center justify-center mx-auto mb-5">
                <QrCode size={32} className="text-[#1F8A70]" />
              </div>
              <h2 className="font-display text-3xl text-[#0A2540] mb-3">Obtén tu QR en 30 segundos</h2>
              <p className="text-[#6F8AA8] max-w-2xl mx-auto mb-6">
                Al registrarte recibirás un <strong>código único</strong> y un <strong>QR</strong> para identificarte
                al entregar residuos. Cada entrega acumulará <strong>kg por tipo</strong> y <strong>puntos PAYT</strong> bonificables.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link
                  to="/ecoparque/registro"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1F8A70] text-white text-sm font-semibold hover:bg-[#177A60] transition-colors"
                  data-testid="eco-dashboard-cta-registro-main"
                >
                  <UserPlus size={16} /> Crear mi cuenta
                </Link>
                <Link
                  to="/ecoparque/login"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#CFE3F5] text-[#0A2540] text-sm font-medium hover:bg-[#F5F9FC] transition-colors"
                  data-testid="eco-dashboard-cta-login-main"
                >
                  <LogIn size={16} /> Ya tengo cuenta
                </Link>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* Panel PAYT */}
          <section className="py-12 px-6">
            <div className="max-w-7xl mx-auto">
              <div className="rounded-3xl border-2 border-[#7DB9E8] bg-gradient-to-br from-[#F5F9FC] to-white p-8 sm:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <Award size={28} className="text-[#1E5AA8]" />
                  <h2 className="font-display text-3xl text-[#0A2540]">Tus estadísticas PAYT</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <StatBlock
                    icon={Recycle}
                    color="#1F8A70"
                    label="Total kg reciclados"
                    value={loading ? '…' : `${total.kg.toFixed(1)} kg`}
                    testid="eco-dashboard-total-kg"
                  />
                  <StatBlock
                    icon={Award}
                    color="#1E5AA8"
                    label="Puntos acumulados"
                    value={loading ? '…' : total.points.toFixed(0)}
                    testid="eco-dashboard-total-points"
                  />
                  <StatBlock
                    icon={TrendingUp}
                    color="#0A2540"
                    label="Total entregas"
                    value={loading ? '…' : total.entries}
                    testid="eco-dashboard-total-entries"
                  />
                </div>

                {/* Desglose por tipo */}
                <div className="mt-8">
                  <h3 className="font-display text-xl text-[#0A2540] mb-4">Desglose por tipo de residuo</h3>
                  {loading ? (
                    <div className="flex items-center gap-2 text-[#6F8AA8]">
                      <Loader2 size={16} className="animate-spin" /> Cargando…
                    </div>
                  ) : stats && stats.by_type.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {stats.by_type.map((row) => (
                        <div
                          key={row.waste_type}
                          className="rounded-2xl border border-[#CFE3F5] bg-white p-4 flex items-center justify-between"
                          data-testid={`eco-dashboard-type-${row.waste_type}`}
                        >
                          <div>
                            <p className="text-sm font-medium text-[#0A2540]">{row.waste_type}</p>
                            <p className="text-xs text-[#6F8AA8]">{row.entries} entrega{row.entries !== 1 ? 's' : ''}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-[#1F8A70] tabular-nums">{row.total_kg.toFixed(1)} kg</p>
                            <p className="text-xs text-[#1E5AA8] tabular-nums">+{row.points.toFixed(0)} pts</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#6F8AA8]">
                      Aún no tienes entregas registradas. Acude al Ecoparque con tu QR para empezar a sumar.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Tabs Entregas / Normas */}
          <section className="py-12 px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
                <TabButton active={activeTab === 'entregas'} onClick={() => setActiveTab('entregas')}>
                  Mis entregas
                </TabButton>
                <TabButton active={activeTab === 'normas'} onClick={() => setActiveTab('normas')}>
                  Normas y consejos
                </TabButton>
              </div>

              <div className="rounded-3xl border border-[#CFE3F5] bg-white p-6 sm:p-8">
                {activeTab === 'entregas' && (
                  <div>
                    <h3 className="font-display text-2xl text-[#0A2540] mb-6 flex items-center gap-2">
                      <Calendar size={24} />
                      Historial de entregas
                    </h3>
                    {loading ? (
                      <div className="flex items-center gap-2 text-[#6F8AA8]">
                        <Loader2 size={16} className="animate-spin" /> Cargando…
                      </div>
                    ) : deliveries.length === 0 ? (
                      <p className="text-sm text-[#6F8AA8]">Sin entregas registradas todavía.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full" data-testid="eco-dashboard-deliveries-table">
                          <thead>
                            <tr className="border-b border-[#CFE3F5]">
                              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A2540]">Fecha</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A2540]">Tipo</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A2540]">Peso</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A2540]">Puntos</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A2540]">Notas</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deliveries.map((d) => (
                              <tr key={d.id} className="border-b border-[#CFE3F5] hover:bg-[#FAFCFE] transition-colors">
                                <td className="py-4 px-4 text-sm text-[#6F8AA8] tabular-nums">
                                  {new Date(d.created_at).toLocaleString('es-ES')}
                                </td>
                                <td className="py-4 px-4 text-sm text-[#0A2540] font-medium">{d.waste_type}</td>
                                <td className="py-4 px-4 text-sm text-[#6F8AA8] tabular-nums">{d.quantity_kg.toFixed(1)} kg</td>
                                <td className="py-4 px-4 text-sm font-semibold text-[#1F8A70] tabular-nums">+{d.points.toFixed(0)}</td>
                                <td className="py-4 px-4 text-sm text-[#6F8AA8]">{d.notes || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'normas' && (
                  <div>
                    <h3 className="font-display text-2xl text-[#0A2540] mb-6 flex items-center gap-2">
                      <Info size={24} />
                      Normas y consejos
                    </h3>
                    <div className="space-y-6">
                      <div className="bg-[#F5F9FC] rounded-2xl p-6">
                        <h4 className="font-semibold text-[#0A2540] mb-3">Qué SÍ puedes traer</h4>
                        <ul className="space-y-2 text-sm text-[#6F8AA8]">
                          {['Papel/Cartón', 'Vidrio', 'Plásticos', 'Orgánico', 'Aceite', 'Pilas', 'Electrónicos', 'Voluminosos', 'Poda'].map((t) => (
                            <li key={t} className="flex items-start gap-2">
                              <span className="text-[#1F8A70] mt-0.5">✓</span>
                              {t}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-[#FAFCFE] rounded-2xl p-6 border border-[#CFE3F5]">
                        <h4 className="font-semibold text-[#0A2540] mb-3">Consejos útiles</h4>
                        <ul className="space-y-2 text-sm text-[#6F8AA8]">
                          <li className="flex items-start gap-2"><span className="text-[#1E5AA8]">→</span>Separa los residuos en casa antes de venir.</li>
                          <li className="flex items-start gap-2"><span className="text-[#1E5AA8]">→</span>Lleva tu QR descargado en el móvil.</li>
                          <li className="flex items-start gap-2"><span className="text-[#1E5AA8]">→</span>Llega con tiempo antes del cierre.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Contacto */}
      <section className="py-12 px-6 bg-[#FAFCFE]">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="font-display text-2xl text-[#0A2540] mb-4">¿Necesitas ayuda?</h3>
          <p className="text-[#6F8AA8] mb-6">
            Contacta con nosotros para resolver cualquier duda sobre el Ecoparque.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:losalcazareslimpio@valorizasm.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#CFE3F5] text-[#0A2540] text-sm font-medium hover:bg-white transition-colors"
            >
              <Mail size={18} />
              losalcazareslimpio@valorizasm.com
            </a>
            <a
              href="tel:+34900000000"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1E5AA8] text-white text-sm font-medium hover:bg-[#0F4C97] transition-colors"
            >
              <Phone size={18} />
              Llamar
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function StatBlock({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="text-center p-6 rounded-2xl bg-white border border-[#CFE3F5]">
      <Icon size={24} style={{ color }} className="mx-auto mb-3" />
      <p className="text-sm text-[#6F8AA8] mb-1">{label}</p>
      <p className="text-4xl font-display tabular-nums" style={{ color }} data-testid={testid}>{value}</p>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-6 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
        active ? 'bg-[#1E5AA8] text-white' : 'bg-[#FAFCFE] text-[#6F8AA8] hover:bg-[#CFE3F5]'
      }`}
    >
      {children}
    </button>
  );
}
